import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProjectGroup, V2ListProjectGroupsByPaidolIdPayload } from 'CDK_API';
import { cardService } from 'app/services/card.service';
import { agaveService } from 'app/services/agave.service';
import { RootState } from 'app/store/rootReducer';
import { getProjectGroups, clearProjectGroups } from 'app/pages/store/projectGroupSlice';
import { setColumnFilters } from 'app/store/filtering/searchAndFilterSlice';
import { ColumnFilterMap, FilterOption } from 'util/filtering/models';
import { mergeColumnFilters } from 'util/filtering/functions';
import {
  buildProjectGroupColumnFiltersFromConfig,
  generateProjectGroupFilterObject,
  ProjectGroupDynamicFilterOptions,
} from 'app/store/filtering/projectGroupFilterConfig';
import { searchFlexCodes } from 'app/pages/store/flexCodingSlice';
import { AppDispatch } from 'app/store';
import { hasErpCodingUI } from 'app/util/paidolAccountType';

// Fetch all project groups via pagination, through the CDK cardService — the same endpoint
// the project-groups list uses. Project groups have no dedicated filter-options resolver, so
// the facets are derived from the list; this was the one path still fetching it from Amplify.
// The CDK resolver excludes soft-deleted groups server-side (so no deletedAt check here).
const fetchAllProjectGroups = async (paidolId: string): Promise<ProjectGroup[]> => {
  let nextToken: string | null | undefined = undefined;
  const allGroups: ProjectGroup[] = [];

  do {
    const payload: V2ListProjectGroupsByPaidolIdPayload = await cardService.v2ListProjectGroupsByPaidolId({
      paidolId,
      nextToken: nextToken ?? undefined,
      limit: 100,
    });

    const items = payload?.items ?? [];
    allGroups.push(...items.filter((group): group is ProjectGroup => !!group));

    nextToken = payload?.nextToken;
  } while (nextToken);

  return allGroups;
};

export const retrieveProjectGroupsFilterOptions = createAsyncThunk(
  'searchAndFilter/projectGroups/retrieveFilterOptions',
  async ({ pageName, paidolId }: { pageName: string; paidolId: string }, { dispatch, getState }) => {
    const state = getState() as RootState;

    let groupAdminOptions: FilterOption[] = [];
    const projectAssociationOptions: FilterOption[] = [];

    const allGroups = await fetchAllProjectGroups(paidolId);

    const groupAdminMap = new Map<string, FilterOption>();
    allGroups.forEach((group) => {
      group.groupAdmins?.items?.forEach((admin) => {
        const paidolUser = admin?.paidolUser;
        if (paidolUser?.id) {
          const name =
            paidolUser.user?.first_name && paidolUser.user?.last_name
              ? `${paidolUser.user.first_name} ${paidolUser.user.last_name}`.trim()
              : paidolUser.email || paidolUser.id;
          groupAdminMap.set(paidolUser.id, { id: paidolUser.id, label: name });
        }
      });
    });
    groupAdminOptions = Array.from(groupAdminMap.values()).sort((a, b) => a.label.localeCompare(b.label));

    const allProjectIds = new Set<string>();
    allGroups.forEach((group) => {
      group.groupProjects?.forEach((projectId) => {
        if (projectId) allProjectIds.add(projectId);
      });
    });

    if (allProjectIds.size > 0) {
      const selectedPaidol = state.userCompanies.selectedPaidol;
      const usesAgaveCoding = hasErpCodingUI(selectedPaidol);

      const existingProjectOptions = (state.searchAndFilter[pageName]?.columnFilters?.projectAssociation
        ?.options ?? []) as FilterOption[];
      const existingLabelMap = new Map(existingProjectOptions.map((o) => [o.id, o.label]));

      try {
        if (!usesAgaveCoding) {
          const result = await (dispatch as AppDispatch)(
            searchFlexCodes({
              paidolId,
              searchTerm: '',
              specificIds: Array.from(allProjectIds),
              isSettingsRequest: true,
            })
          ).unwrap();
          const projects = (result as { items?: Array<{ id: string; projectName?: string }> })?.items;
          if (projects && Array.isArray(projects)) {
            const projectLookup = new Map(projects.map((p) => [p.id, p.projectName || p.id]));
            allProjectIds.forEach((id) => {
              projectAssociationOptions.push({
                id,
                label: projectLookup.get(id) || existingLabelMap.get(id) || id,
              });
            });
          }
        } else {
          const result = await agaveService.v2SearchAgaveProjects({
            input: {
              filter: { paidolId, ids: Array.from(allProjectIds) },
              limit: allProjectIds.size,
            },
          });
          const projects = result?.items || [];
          const projectLookup = new Map(projects.map((p) => [p.id, p.fullName || p.name || p.id]));
          allProjectIds.forEach((id) => {
            projectAssociationOptions.push({
              id,
              label: projectLookup.get(id) || existingLabelMap.get(id) || id,
            });
          });
        }
      } catch {
        allProjectIds.forEach((id) => {
          projectAssociationOptions.push({ id, label: existingLabelMap.get(id) || id });
        });
      }

      projectAssociationOptions.sort((a, b) => a.label.localeCompare(b.label));
    }

    const dynamicOptions: ProjectGroupDynamicFilterOptions = {
      groupAdmin: groupAdminOptions,
      projectAssociation: projectAssociationOptions,
    };

    const columnFilters = buildProjectGroupColumnFiltersFromConfig(pageName, dynamicOptions);

    const searchAndFilter = state.searchAndFilter[pageName];
    const existingFilters = searchAndFilter?.columnFilters || {};

    const mergedFilters = mergeColumnFilters(existingFilters, columnFilters);

    dispatch(setColumnFilters({ pageName, columnFilters: mergedFilters }));

    return { pageName };
  }
);

export const applyProjectGroupsColumnFilters = createAsyncThunk(
  'searchAndFilter/projectGroups/applyColumnFilters',
  async (
    { pageName, columnFilters }: { pageName: string; columnFilters: ColumnFilterMap },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    dispatch(setColumnFilters({ pageName, columnFilters }));

    const filter = generateProjectGroupFilterObject(columnFilters);
    const searchAndFilter = state.searchAndFilter[pageName];
    const sort = searchAndFilter?.sortOption?.field ? searchAndFilter.sortOption : undefined;

    dispatch(clearProjectGroups());

    dispatch(
      getProjectGroups({
        paidolId,
        limit: 100,
        sort,
        filter,
      })
    );

    return { pageName, columnFilters };
  }
);

export const applyProjectGroupsSortFilter = createAsyncThunk(
  'searchAndFilter/projectGroups/applySortFilter',
  async (
    { pageName, sortOption }: { pageName: string; sortOption: { field: string; direction: string } },
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    const columnFilters = state.searchAndFilter[pageName]?.columnFilters || {};
    const filter = generateProjectGroupFilterObject(columnFilters);

    dispatch(clearProjectGroups());

    dispatch(
      getProjectGroups({
        paidolId,
        limit: 100,
        sort: sortOption,
        filter,
      })
    );

    return { pageName, sortOption };
  }
);

export const clearProjectGroupsColumnFilters = createAsyncThunk(
  'searchAndFilter/projectGroups/clearColumnFilters',
  async ({ pageName }: { pageName: string }, { dispatch, getState }) => {
    const state = getState() as RootState;
    const paidolId = state.userCompanies.selectedPaidolId;

    if (!paidolId) {
      return;
    }

    dispatch(clearProjectGroups());

    const searchAndFilter = state.searchAndFilter[pageName];
    const sort = searchAndFilter?.sortOption?.field ? searchAndFilter.sortOption : undefined;

    dispatch(
      getProjectGroups({
        paidolId,
        limit: 100,
        sort,
      })
    );

    return { pageName };
  }
);
